'use client';

import React, { useCallback, useState } from 'react';
import { Box } from '@mui/material';
import {
  InquiryFlowLoader,
  InquiryFlowSkeleton,
  type FlowDocument,
  type InquiryFlowSnapshot,
} from '@miso/design-system';

/**
 * 단일-flow step 어댑터의 공통 셸. InquiryFlowLoader 인스턴스를 마운트하고 wrapperRef
 * 를 BottomSheet container 로 전달한다. 각 step adapter 는 fetcher 와
 * onComplete/onCancel/onError 만 정의하면 된다.
 *
 * 마이그레이션 메모: 이전에는 어댑터가 빌더로 만든 FlowDocument 를 prop 으로 전달
 * 했지만, Firestore `playground_flows` 컬렉션 등록 (`scripts/register-flows.ts`)
 * 후로는 어댑터가 `flowId` 만 알고 fetcher 를 통해 비동기 로드한다.
 * `CounselStartFlowAdapter` / `HouseStructureFlowAdapter` 와 동일한 패턴.
 *
 * `startAtQuestionId` — 이전 sub-stage 로 뒤로가기 한 경우, 첫 질문이 아닌
 * 마지막 visible 질문부터 보여주기 위해 InquiryFlow 에 전달된다.
 */
export interface StepFlowProps {
  /**
   * FlowDocument 를 비동기로 반환하는 fetcher. `createProgressFlowFetcher(flowId)`
   * 로 안정된 참조를 만든 뒤 useMemo([]) 로 감싸 사용하는 것을 권장한다 — fetcher
   * identity 가 매 렌더 새로 만들어지면 InquiryFlowLoader 가 매번 re-fetch 한다.
   */
  fetcher: () => Promise<FlowDocument>;
  initialValues?: Record<string, unknown>;
  onComplete: (values: Record<string, unknown>) => Promise<void> | void;
  onCancel: () => void;
  onError?: (err: Error) => void;
  /** 진입 시작 질문 id (없으면 0번부터). 뒤로가기 진입 시 마지막 활성 질문 지정용. */
  startAtQuestionId?: string | null;
  progressOffset?: number;
  progressTotal?: number;
  onStateChange?: (snap: InquiryFlowSnapshot) => void;
}

export function StepFlow({
  fetcher,
  initialValues,
  onComplete,
  onCancel,
  onError,
  startAtQuestionId,
  progressOffset,
  progressTotal,
  onStateChange,
}: StepFlowProps) {
  // useRef 대신 useState — ref callback이 DOM 마운트 후 호출되면 state update로
  // 리렌더가 발생해 InquiryFlowLoader에 실제 container 엘리먼트가 전달된다.
  // useRef는 리렌더를 트리거하지 않아 container가 항상 null로 전달되는 버그가 있었음.
  const [container, setContainer] = useState<HTMLElement | null>(null);
  // ref callback을 useCallback으로 안정화 — 새 함수 참조가 생길 때마다 React가
  // 이전 callback(null)→새 callback(element) 순으로 호출해 flicker가 발생하는 것을 방지.
  const containerRef = useCallback((el: HTMLDivElement | null) => {
    setContainer(el);
  }, []);

  return (
    <Box ref={containerRef} sx={{ position: 'relative', height: '100%', overflow: 'hidden' }}>
      <InquiryFlowLoader
        fetcher={fetcher}
        initialValues={initialValues}
        registries={{ terminals: {} }}
        onComplete={async (values) => {
          await onComplete(values);
        }}
        onCancel={onCancel}
        onError={onError}
        container={container}
        startAtQuestionId={startAtQuestionId}
        skeleton={<InquiryFlowSkeleton />}
        progressOffset={progressOffset}
        progressTotal={progressTotal}
        onStateChange={onStateChange}
      />
    </Box>
  );
}
